import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { Draft } from 'immer';
import type { SelectedItem } from '@/features/quote-and-apply-builder';
import { deepCloneSection, deepCloneSubsection } from '../utils/duplicate-helpers';

interface Position {
  sectionIndex: number;
  subsectionIndex: number;
}

interface FormBuilderState {
  formDefinition: PolicyFormDefinition;
  formMetadata: PolicyFormMetadata;
  selectedItem: SelectedItem | null;
  isDirty: boolean;
  currentSectionIndex: number;
  currentSubsectionIndex: number;
}

interface FormBuilderActions {
  initialize: (formConfig: PolicyFormConfig) => void;
  reset: () => void;

  setFormMetadata: (metadata: PolicyFormMetadata) => void;
  setSelectedItem: (item: SelectedItem | null) => void;
  resetDirty: () => void;

  addSection: (section: PolicyFormSection) => void;
  updateSection: (sectionId: string, updates: Partial<PolicyFormSection>) => void;
  removeSection: (sectionId: string) => void;

  addSubsection: (sectionId: string, subsection: PolicyFormSubSection) => void;
  updateSubsection: (
    sectionId: string,
    subsectionId: string,
    updates: Partial<PolicyFormSubSection>,
  ) => void;
  removeSubsection: (sectionId: string, subsectionId: string) => void;

  addGroup: (sectionId: string, subsectionId: string, group: PolicyFormQuestionGroup) => void;
  updateGroup: (
    sectionId: string,
    subsectionId: string,
    groupId: string,
    updates: Partial<PolicyFormQuestionGroup>,
  ) => void;
  removeGroup: (sectionId: string, subsectionId: string, groupId: string) => void;

  addQuestion: (
    sectionId: string,
    subsectionId: string,
    groupId: string,
    question: PolicyFormQuestion,
  ) => void;
  updateQuestion: (questionRef: string, updates: Partial<PolicyFormQuestion>) => void;
  removeQuestion: (
    sectionId: string,
    subsectionId: string,
    groupId: string,
    questionRef: string,
  ) => void;

  navigateTo: (sectionIndex: number, subsectionIndex: number) => void;
  navigateNext: () => void;
  navigatePrevious: () => void;

  duplicateSection: (sectionIndex: number) => void;
  duplicateSubsection: (sectionIndex: number, subsectionIndex: number) => void;

  moveSection: (fromIndex: number, toIndex: number) => void;
  moveSubsection: (sectionId: string, fromIndex: number, toIndex: number) => void;
  moveGroup: (
    sectionId: string,
    subsectionId: string,
    fromIndex: number,
    toIndex: number,
  ) => void;
  moveQuestion: (
    sectionId: string,
    subsectionId: string,
    groupId: string,
    fromIndex: number,
    toIndex: number,
  ) => void;

  exportForm: () => void;
  exportSection: (sectionIndex: number) => void;
  importForm: (file: File) => void;
}

type FormBuilderStore = FormBuilderState & FormBuilderActions;

// --- Helpers ---

const createDefaultFormDefinition = (): PolicyFormDefinition => ({
  sections: [],
  questions: {},
  requiredParameters: [],
});

const createDefaultFormMetadata = (): PolicyFormMetadata => ({
  formId: crypto.randomUUID(),
  templateId: '',
  formType: 'application-form',
  title: 'New Form',
  carrier: '',
  carrierEmail: '',
  product: '',
  totalQuestions: 0,
  roleKeys: [],
});

const initialState: FormBuilderState = {
  formDefinition: createDefaultFormDefinition(),
  formMetadata: createDefaultFormMetadata(),
  selectedItem: null,
  isDirty: false,
  currentSectionIndex: 0,
  currentSubsectionIndex: 0,
};

function findSection(draft: Draft<PolicyFormDefinition>, sectionId: string) {
  return draft.sections.find(section => section.id === sectionId);
}

function findSubsection(section: Draft<PolicyFormSection> | undefined, subsectionId: string) {
  return section?.subsections.find(subsection => subsection.id === subsectionId);
}

function findGroup(subsection: Draft<PolicyFormSubSection> | undefined, groupId: string) {
  return subsection?.questionGroups.find(group => group.id === groupId);
}

function removeQuestionRefs(draft: Draft<PolicyFormDefinition>, refs: Set<string>) {
  refs.forEach(ref => delete draft.questions[ref]);
}

function collectGroupQuestionRefs(group: Draft<PolicyFormQuestionGroup>): Set<string> {
  return new Set(group.questions);
}

function collectSubsectionQuestionRefs(subsection: Draft<PolicyFormSubSection>): Set<string> {
  const refs = new Set<string>();
  subsection.questionGroups.forEach(group => {
    group.questions.forEach(questionRef => refs.add(questionRef));
  });
  return refs;
}

function collectSectionQuestionRefs(section: Draft<PolicyFormSection>): Set<string> {
  const refs = new Set<string>();
  section.subsections.forEach(subsection => {
    subsection.questionGroups.forEach(group => {
      group.questions.forEach(questionRef => refs.add(questionRef));
    });
  });
  return refs;
}

function computeFlatPositions(formDefinition: PolicyFormDefinition): Position[] {
  const positions: Position[] = [];
  formDefinition.sections.forEach((section, sectionIdx) => {
    section.subsections.forEach((_, subIdx) => {
      positions.push({ sectionIndex: sectionIdx, subsectionIndex: subIdx });
    });
  });
  return positions;
}

function downloadJson(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

// --- Store ---

export const useFormBuilderStore = create<FormBuilderStore>()(
  immer((set, get) => ({
    ...initialState,

    initialize: formConfig => {
      set(state => {
        state.formDefinition = formConfig.formDefinition;
        state.formMetadata = formConfig.formMetadata;
        state.selectedItem = null;
        state.isDirty = false;
        state.currentSectionIndex = 0;
        state.currentSubsectionIndex = 0;
      });
    },

    reset: () => {
      set(initialState);
    },

    setFormMetadata: metadata => {
      set(state => {
        state.formMetadata = metadata;
        state.isDirty = true;
      });
    },

    setSelectedItem: item => {
      set(state => {
        state.selectedItem = item;
      });
    },

    resetDirty: () => {
      set(state => {
        state.isDirty = false;
      });
    },

    // Section CRUD

    addSection: section => {
      set(state => {
        state.formDefinition.sections.push(section);
        state.selectedItem = { type: 'section', id: section.id };
        state.isDirty = true;
      });
    },

    updateSection: (sectionId, updates) => {
      set(state => {
        const section = findSection(state.formDefinition, sectionId);
        if (section) Object.assign(section, updates);
        state.isDirty = true;
      });
    },

    removeSection: sectionId => {
      set(state => {
        const section = findSection(state.formDefinition, sectionId);
        if (section) removeQuestionRefs(state.formDefinition, collectSectionQuestionRefs(section));
        state.formDefinition.sections = state.formDefinition.sections.filter(
          s => s.id !== sectionId,
        );
        state.selectedItem = null;
        state.isDirty = true;
        if (state.currentSectionIndex >= state.formDefinition.sections.length) {
          state.currentSectionIndex = Math.max(0, state.formDefinition.sections.length - 1);
          state.currentSubsectionIndex = 0;
        }
      });
    },

    // Subsection CRUD

    addSubsection: (sectionId, subsection) => {
      set(state => {
        const section = findSection(state.formDefinition, sectionId);
        if (section) section.subsections.push(subsection);
        state.selectedItem = { type: 'subsection', id: subsection.id, sectionId };
        state.isDirty = true;
      });
    },

    updateSubsection: (sectionId, subsectionId, updates) => {
      set(state => {
        const subsection = findSubsection(
          findSection(state.formDefinition, sectionId),
          subsectionId,
        );
        if (subsection) Object.assign(subsection, updates);
        state.isDirty = true;
      });
    },

    removeSubsection: (sectionId, subsectionId) => {
      set(state => {
        const section = findSection(state.formDefinition, sectionId);
        const subsection = findSubsection(section, subsectionId);
        if (subsection)
          removeQuestionRefs(state.formDefinition, collectSubsectionQuestionRefs(subsection));
        if (section)
          section.subsections = section.subsections.filter(sub => sub.id !== subsectionId);
        state.selectedItem = null;
        state.isDirty = true;
        const currentSection = state.formDefinition.sections[state.currentSectionIndex];
        if (currentSection && state.currentSubsectionIndex >= currentSection.subsections.length) {
          state.currentSubsectionIndex = Math.max(0, currentSection.subsections.length - 1);
        }
      });
    },

    // Group CRUD

    addGroup: (sectionId, subsectionId, group) => {
      set(state => {
        const subsection = findSubsection(
          findSection(state.formDefinition, sectionId),
          subsectionId,
        );
        if (subsection) subsection.questionGroups.push(group);
        state.selectedItem = { type: 'group', id: group.id, sectionId, subsectionId };
        state.isDirty = true;
      });
    },

    updateGroup: (sectionId, subsectionId, groupId, updates) => {
      set(state => {
        const group = findGroup(
          findSubsection(findSection(state.formDefinition, sectionId), subsectionId),
          groupId,
        );
        if (group) Object.assign(group, updates);
        state.isDirty = true;
      });
    },

    removeGroup: (sectionId, subsectionId, groupId) => {
      set(state => {
        const subsection = findSubsection(
          findSection(state.formDefinition, sectionId),
          subsectionId,
        );
        const group = findGroup(subsection, groupId);
        if (group) removeQuestionRefs(state.formDefinition, collectGroupQuestionRefs(group));
        if (subsection)
          subsection.questionGroups = subsection.questionGroups.filter(grp => grp.id !== groupId);
        state.selectedItem = null;
        state.isDirty = true;
      });
    },

    // Question CRUD

    addQuestion: (sectionId, subsectionId, groupId, question) => {
      set(state => {
        const group = findGroup(
          findSubsection(findSection(state.formDefinition, sectionId), subsectionId),
          groupId,
        );
        if (group) group.questions.push(question.questionRef);
        state.formDefinition.questions[question.questionRef] = question;
        state.selectedItem = {
          type: 'question',
          id: question.id,
          sectionId,
          subsectionId,
          groupId,
          questionRef: question.questionRef,
        };
        state.isDirty = true;
      });
    },

    updateQuestion: (questionRef, updates) => {
      set(state => {
        const newRef = updates.questionRef;
        if (newRef && newRef !== questionRef) {
          const question = state.formDefinition.questions[questionRef];
          if (!question) return;
          Object.assign(question, updates);
          state.formDefinition.questions[newRef] = question;
          delete state.formDefinition.questions[questionRef];
          state.formDefinition.sections.forEach(section => {
            section.subsections.forEach(subsection => {
              subsection.questionGroups.forEach(group => {
                const idx = group.questions.indexOf(questionRef);
                if (idx !== -1) group.questions[idx] = newRef;
              });
            });
          });
          if (state.selectedItem?.type === 'question' && state.selectedItem.questionRef === questionRef) {
            state.selectedItem.questionRef = newRef;
            state.selectedItem.id = newRef;
          }
        } else {
          Object.assign(state.formDefinition.questions[questionRef], updates);
        }
        state.isDirty = true;
      });
    },

    removeQuestion: (sectionId, subsectionId, groupId, questionRef) => {
      set(state => {
        const group = findGroup(
          findSubsection(findSection(state.formDefinition, sectionId), subsectionId),
          groupId,
        );
        if (group) group.questions = group.questions.filter(ref => ref !== questionRef);
        delete state.formDefinition.questions[questionRef];
        state.selectedItem = null;
        state.isDirty = true;
      });
    },

    // Navigation

    navigateTo: (sectionIndex, subsectionIndex) => {
      set(state => {
        state.currentSectionIndex = sectionIndex;
        state.currentSubsectionIndex = subsectionIndex;
      });
    },

    navigateNext: () => {
      const { formDefinition, currentSectionIndex, currentSubsectionIndex } = get();
      const positions = computeFlatPositions(formDefinition);
      const idx = positions.findIndex(
        p => p.sectionIndex === currentSectionIndex && p.subsectionIndex === currentSubsectionIndex,
      );
      if (idx < positions.length - 1) {
        const next = positions[idx + 1];
        set(state => {
          state.currentSectionIndex = next.sectionIndex;
          state.currentSubsectionIndex = next.subsectionIndex;
        });
      }
    },

    navigatePrevious: () => {
      const { formDefinition, currentSectionIndex, currentSubsectionIndex } = get();
      const positions = computeFlatPositions(formDefinition);
      const idx = positions.findIndex(
        p => p.sectionIndex === currentSectionIndex && p.subsectionIndex === currentSubsectionIndex,
      );
      if (idx > 0) {
        const prev = positions[idx - 1];
        set(state => {
          state.currentSectionIndex = prev.sectionIndex;
          state.currentSubsectionIndex = prev.subsectionIndex;
        });
      }
    },

    // Duplicate

    duplicateSection: sectionIndex => {
      const { formDefinition } = get();
      const source = formDefinition.sections[sectionIndex];
      if (!source) return;

      const existingRefs = new Set(Object.keys(formDefinition.questions));
      const { section: clonedSection, questions: clonedQuestions } = deepCloneSection(
        source,
        formDefinition.questions,
        existingRefs,
      );

      set(state => {
        Object.assign(state.formDefinition.questions, clonedQuestions);
        state.formDefinition.sections.splice(sectionIndex + 1, 0, clonedSection);
        state.currentSectionIndex = sectionIndex + 1;
        state.currentSubsectionIndex = 0;
        state.selectedItem = { type: 'section', id: clonedSection.id };
        state.isDirty = true;
      });
    },

    duplicateSubsection: (sectionIndex, subsectionIndex) => {
      const { formDefinition } = get();
      const section = formDefinition.sections[sectionIndex];
      const source = section?.subsections[subsectionIndex];
      if (!section || !source) return;

      const existingRefs = new Set(Object.keys(formDefinition.questions));
      const { subsection: clonedSubsection, questions: clonedQuestions } = deepCloneSubsection(
        source,
        formDefinition.questions,
        existingRefs,
      );

      set(state => {
        Object.assign(state.formDefinition.questions, clonedQuestions);
        const targetSection = state.formDefinition.sections[sectionIndex];
        if (targetSection) {
          targetSection.subsections.splice(subsectionIndex + 1, 0, clonedSubsection);
        }
        state.currentSectionIndex = sectionIndex;
        state.currentSubsectionIndex = subsectionIndex + 1;
        state.selectedItem = {
          type: 'subsection',
          id: clonedSubsection.id,
          sectionId: section.id,
        };
        state.isDirty = true;
      });
    },

    // Move / Reorder

    moveSection: (fromIndex, toIndex) => {
      set(state => {
        const sections = state.formDefinition.sections;
        const [moved] = sections.splice(fromIndex, 1);
        sections.splice(toIndex, 0, moved);
        if (state.currentSectionIndex === fromIndex) {
          state.currentSectionIndex = toIndex;
        } else if (fromIndex < state.currentSectionIndex && toIndex >= state.currentSectionIndex) {
          state.currentSectionIndex -= 1;
        } else if (fromIndex > state.currentSectionIndex && toIndex <= state.currentSectionIndex) {
          state.currentSectionIndex += 1;
        }
        state.isDirty = true;
      });
    },

    moveSubsection: (sectionId, fromIndex, toIndex) => {
      set(state => {
        const section = findSection(state.formDefinition, sectionId);
        if (!section) return;
        const [moved] = section.subsections.splice(fromIndex, 1);
        section.subsections.splice(toIndex, 0, moved);
        const sectionIndex = state.formDefinition.sections.indexOf(section);
        if (state.currentSectionIndex === sectionIndex) {
          if (state.currentSubsectionIndex === fromIndex) {
            state.currentSubsectionIndex = toIndex;
          } else if (
            fromIndex < state.currentSubsectionIndex &&
            toIndex >= state.currentSubsectionIndex
          ) {
            state.currentSubsectionIndex -= 1;
          } else if (
            fromIndex > state.currentSubsectionIndex &&
            toIndex <= state.currentSubsectionIndex
          ) {
            state.currentSubsectionIndex += 1;
          }
        }
        state.isDirty = true;
      });
    },

    moveGroup: (sectionId, subsectionId, fromIndex, toIndex) => {
      set(state => {
        const subsection = findSubsection(
          findSection(state.formDefinition, sectionId),
          subsectionId,
        );
        if (!subsection) return;
        const [moved] = subsection.questionGroups.splice(fromIndex, 1);
        subsection.questionGroups.splice(toIndex, 0, moved);
        state.isDirty = true;
      });
    },

    moveQuestion: (sectionId, subsectionId, groupId, fromIndex, toIndex) => {
      set(state => {
        const group = findGroup(
          findSubsection(findSection(state.formDefinition, sectionId), subsectionId),
          groupId,
        );
        if (!group) return;
        const [moved] = group.questions.splice(fromIndex, 1);
        group.questions.splice(toIndex, 0, moved);
        state.isDirty = true;
      });
    },

    // Import/Export

    exportForm: () => {
      const { formMetadata, formDefinition } = get();
      const exportData = { formMetadata, formDefinition };
      downloadJson(
        exportData,
        `${formMetadata.title.replace(/\s+/g, '-').toLowerCase()}-form.json`,
      );
    },

    exportSection: sectionIndex => {
      const { formDefinition } = get();
      const section = formDefinition.sections[sectionIndex];
      if (!section) return;

      const questionRefs = new Set<string>();
      section.subsections.forEach(sub => {
        sub.questionGroups.forEach(group => {
          group.questions.forEach(ref => questionRefs.add(ref));
        });
      });

      const sectionQuestions: Record<string, PolicyFormQuestion> = {};
      questionRefs.forEach(ref => {
        if (formDefinition.questions[ref]) {
          sectionQuestions[ref] = formDefinition.questions[ref];
        }
      });

      const exportData = { section, questions: sectionQuestions };
      const slug = (section.title || 'section').replace(/\s+/g, '-').toLowerCase();
      downloadJson(exportData, `${slug}-section.json`);
    },

    importForm: file => {
      const reader = new FileReader();
      reader.onload = event => {
        try {
          const data = JSON.parse(event.target?.result as string);
          set(state => {
            if (data.formMetadata) state.formMetadata = data.formMetadata;
            if (data.formDefinition) state.formDefinition = data.formDefinition;
            state.isDirty = false;
            state.selectedItem = null;
            state.currentSectionIndex = 0;
            state.currentSubsectionIndex = 0;
          });
        } catch (error) {
          console.error('Failed to parse import file:', error);
        }
      };
      reader.readAsText(file);
    },
  })),
);

// --- Selector hooks ---

export const useFormDefinition = () => useFormBuilderStore(state => state.formDefinition);
export const useFormMetadata = () => useFormBuilderStore(state => state.formMetadata);
export const useSelectedItem = () => useFormBuilderStore(state => state.selectedItem);
export const useFormIsDirty = () => useFormBuilderStore(state => state.isDirty);
export const useFormQuestions = () => useFormBuilderStore(state => state.formDefinition.questions);

export const useQuestionSectionMap = () =>
  useFormBuilderStore(state => {
    const map: Record<string, string> = {};
    for (const section of state.formDefinition.sections) {
      for (const subsection of section.subsections) {
        for (const group of subsection.questionGroups) {
          for (const ref of group.questions) {
            map[ref] = section.title;
          }
        }
      }
    }
    return map;
  });
export const useCurrentSectionIndex = () => useFormBuilderStore(state => state.currentSectionIndex);
export const useCurrentSubsectionIndex = () =>
  useFormBuilderStore(state => state.currentSubsectionIndex);

export const useCurrentSection = () =>
  useFormBuilderStore(state => state.formDefinition.sections[state.currentSectionIndex]);

export const useCurrentSubsection = () =>
  useFormBuilderStore(state => {
    const section = state.formDefinition.sections[state.currentSectionIndex];
    return section?.subsections[state.currentSubsectionIndex];
  });

export const useCanGoNext = () =>
  useFormBuilderStore(state => {
    const positions = computeFlatPositions(state.formDefinition);
    const currentIndex = positions.findIndex(
      position =>
        position.sectionIndex === state.currentSectionIndex &&
        position.subsectionIndex === state.currentSubsectionIndex,
    );
    return currentIndex < positions.length - 1;
  });

export const useCanGoPrevious = () =>
  useFormBuilderStore(state => {
    const positions = computeFlatPositions(state.formDefinition);
    const currentIndex = positions.findIndex(
      position =>
        position.sectionIndex === state.currentSectionIndex &&
        position.subsectionIndex === state.currentSubsectionIndex,
    );
    return currentIndex > 0;
  });

// Non-reactive action accessor
export const getFormBuilderActions = () => {
  const store = useFormBuilderStore.getState();
  return {
    initialize: store.initialize,
    reset: store.reset,
    setFormMetadata: store.setFormMetadata,
    setSelectedItem: store.setSelectedItem,
    resetDirty: store.resetDirty,
    addSection: store.addSection,
    updateSection: store.updateSection,
    removeSection: store.removeSection,
    addSubsection: store.addSubsection,
    updateSubsection: store.updateSubsection,
    removeSubsection: store.removeSubsection,
    addGroup: store.addGroup,
    updateGroup: store.updateGroup,
    removeGroup: store.removeGroup,
    addQuestion: store.addQuestion,
    updateQuestion: store.updateQuestion,
    removeQuestion: store.removeQuestion,
    navigateTo: store.navigateTo,
    navigateNext: store.navigateNext,
    navigatePrevious: store.navigatePrevious,
    duplicateSection: store.duplicateSection,
    duplicateSubsection: store.duplicateSubsection,
    moveSection: store.moveSection,
    moveSubsection: store.moveSubsection,
    moveGroup: store.moveGroup,
    moveQuestion: store.moveQuestion,
    exportForm: store.exportForm,
    exportSection: store.exportSection,
    importForm: store.importForm,
  };
};
